import { Tooltip } from '@/components/ui/tooltip';
import { Box, HStack, IconButton } from '@chakra-ui/react';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode, $createHeadingNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType } from '@lexical/utils';

import {
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type ElementFormatType,
  type TextFormatType,
} from 'lexical';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Attachment,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link,
  List,
  NumberedListLeft,
  TextSize,
  Underline,
} from 'iconoir-react';

const ICON_SIZE = 15;
const ICON_STROKE = 2;

interface ToolbarButtonProps {
  isActive: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
}

function ToolbarButton({
  isActive,
  onClick,
  ariaLabel,
  children,
  disabled,
  loading,
  tooltip,
}: ToolbarButtonProps): ReactElement {
  const button = (
    <IconButton
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      loading={loading}
      size="xs"
      w={6}
      h={6}
      minW={6}
      p={0}
      variant={isActive ? 'solid' : 'ghost'}
      colorPalette="primary"
      onClick={onClick}
      rounded="sm"
    >
      {children}
    </IconButton>
  );
  return tooltip ? <Tooltip content={tooltip}>{button}</Tooltip> : button;
}

function ToolbarSeparator() {
  return <Box w="1px" h={4} bg="border" mx={1} />;
}

export interface ToolbarAttachmentControl {
  onFileSelect: (files: FileList | null) => void;
  ariaLabel: string;
  accept: string;
  disabled?: boolean;
  isUploading?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
}

interface ToolbarPluginProps {
  trailingItems?: ReactNode;
  attachmentControl?: ToolbarAttachmentControl;
}

export function ToolbarPlugin({
  trailingItems,
  attachmentControl,
}: ToolbarPluginProps = {}): ReactElement {
  const localFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = attachmentControl?.inputRef ?? localFileInputRef;
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [isLink, setIsLink] = useState(false);
  const [listType, setListType] = useState<'bullet' | 'number' | null>(null);
  const [isHeading, setIsHeading] = useState(false);
  const [alignment, setAlignment] = useState<ElementFormatType>('left');

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setFormatState({
        bold: selection.hasFormat('bold'),
        italic: selection.hasFormat('italic'),
        underline: selection.hasFormat('underline'),
      });

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      const matchingParent = $findMatchingParent(node, $isLinkNode);
      setIsLink($isLinkNode(parent) || $isLinkNode(node) || matchingParent !== null);

      // List detection
      const listNode = $getNearestNodeOfType(node, ListNode);
      setListType(listNode ? (listNode.getListType() === 'number' ? 'number' : 'bullet') : null);

      // Heading + alignment detection
      const topElement =
        node.getKey() === 'root'
          ? node
          : $findMatchingParent(node, (n) => n.getParent()?.getKey() === 'root');
      if (topElement) {
        setIsHeading($isHeadingNode(topElement));
        setAlignment(
          $isElementNode(topElement)
            ? (topElement.getFormatType() as ElementFormatType) || 'left'
            : 'left'
        );
      } else {
        setIsHeading(false);
        setAlignment('left');
      }
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatText = useCallback(
    (format: TextFormatType) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [editor]
  );

  const toggleLink = useCallback(() => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      const url = prompt('Enter URL:');
      if (!url) return;
      const formattedUrl =
        url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
      try {
        const parsed = new URL(formattedUrl);
        if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) return;
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, formattedUrl);
      } catch {
        // Invalid URL, ignore
      }
    }
  }, [editor, isLink]);

  const toggleBulletList = useCallback(() => {
    if (listType === 'bullet') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, listType]);

  const toggleNumberedList = useCallback(() => {
    if (listType === 'number') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, listType]);

  const toggleHeading = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (isHeading) {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode('h2'));
        }
      }
    });
  }, [editor, isHeading]);

  const formatAlignment = useCallback(
    (align: ElementFormatType) => {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
    },
    [editor]
  );

  return (
    <Box w="full" maxW="full" overflow="hidden" borderBottom="1px solid" borderColor="border">
      <HStack w="full" minW={0} gap={1} rowGap={1.5} px={2} py={1} flexWrap="wrap">
        <ToolbarButton
          isActive={formatState.bold}
          onClick={() => formatText('bold')}
          ariaLabel={t('editor.toolbar.bold')}
        >
          <Bold width={ICON_SIZE} height={ICON_SIZE} strokeWidth={2.6} />
        </ToolbarButton>
        <ToolbarButton
          isActive={formatState.italic}
          onClick={() => formatText('italic')}
          ariaLabel={t('editor.toolbar.italic')}
        >
          <Italic width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>
        <ToolbarButton
          isActive={formatState.underline}
          onClick={() => formatText('underline')}
          ariaLabel={t('editor.toolbar.underline')}
        >
          <Underline width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          isActive={isHeading}
          onClick={toggleHeading}
          ariaLabel={t('editor.toolbar.heading')}
        >
          <TextSize width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          isActive={listType === 'bullet'}
          onClick={toggleBulletList}
          ariaLabel={t('editor.toolbar.bulletList')}
        >
          <List width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>
        <ToolbarButton
          isActive={listType === 'number'}
          onClick={toggleNumberedList}
          ariaLabel={t('editor.toolbar.numberedList')}
        >
          <NumberedListLeft width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          isActive={alignment === 'left'}
          onClick={() => formatAlignment('left')}
          ariaLabel={t('editor.toolbar.alignLeft')}
        >
          <AlignLeft width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>
        <ToolbarButton
          isActive={alignment === 'center'}
          onClick={() => formatAlignment('center')}
          ariaLabel={t('editor.toolbar.alignCenter')}
        >
          <AlignCenter width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>
        <ToolbarButton
          isActive={alignment === 'right'}
          onClick={() => formatAlignment('right')}
          ariaLabel={t('editor.toolbar.alignRight')}
        >
          <AlignRight width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ToolbarButton>

        <ToolbarSeparator />

        <HStack gap="2px" flexShrink={0}>
          <ToolbarButton
            isActive={isLink}
            onClick={toggleLink}
            ariaLabel={t('editor.toolbar.link')}
          >
            <Link width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
          </ToolbarButton>
          {attachmentControl && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={attachmentControl.accept}
                onChange={(event) => {
                  attachmentControl.onFileSelect(event.target.files);
                  event.target.value = '';
                }}
              />
              <ToolbarButton
                isActive={false}
                ariaLabel={attachmentControl.ariaLabel}
                tooltip={attachmentControl.ariaLabel}
                onClick={() => fileInputRef.current?.click()}
                disabled={attachmentControl.disabled || attachmentControl.isUploading}
                loading={attachmentControl.isUploading}
              >
                <Attachment width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />
              </ToolbarButton>
            </>
          )}
        </HStack>

        {trailingItems && (
          <>
            <ToolbarSeparator />
            {trailingItems}
          </>
        )}
      </HStack>
    </Box>
  );
}
